/**
 *  @swagger
 *  definitions:
 *    RegattaSponsorParams:
 *      type: object
 *      properties:
 *        RegattaId:
 *          description: ID of the associated Regatta
 *          type: string
 *        Name:
 *          description: Name of the RegattaSponsor
 *          type: string
 *        Link:
 *          description: Link of the RegattaSponsor
 *          type: string
 *        Image:
 *          description: Image of the RegattaSponsor
 *          type: string
 *
 *  @swagger
 *  definitions:
 *    RegattaSponsorCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaSponsorParams'
 *        - type: object
 *          required:
 *            - RegattaId
 *            - Name
 *            - Link
 *            - Image
 *
 *  @swagger
 *  definitions:
 *    RegattaSponsorQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaSponsor
 *              type: string
 *        - $ref: '#/definitions/RegattaSponsorParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaSponsorUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaSponsorQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaSponsor:
 *      allOf:
 *        - $ref: '#/definitions/RegattaSponsorCreateParams'
 *        - $ref: '#/definitions/RegattaSponsorUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaSponsor:
 *        allOf:
 *          - $ref: '#/definitions/RegattaSponsor'
 *          - type: object
 *            properties:
 *              Regatta:
 *                description: Regatta of the RegattaSponsor
 *                $ref: '#/components/schemas/Regatta'
 */

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegattaId: string;
};

export type $Params = {
  Image: string;
  Link: string;
  Name: string;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Regatta?: $Model<'Regatta'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Image: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Link: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaSponsor'> => {
  const RegattaSponsor = stack.define(
    'RegattaSponsor',
    fields(Sequelize),
    {
      tableName: 'RegattaSponsors',
    },
  );

  RegattaSponsor.associate = (models: $ModelClasses) => {
    models.RegattaSponsor.belongsTo(
      models.Regatta,
      {
        as: 'Regatta',
        foreignKey: 'RegattaId',
      },
    );
  };

  RegattaSponsor.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return RegattaSponsor;
});
